import { useRef } from "react";
import toast from 'react-hot-toast';
import { MdEmail, MdPhone, MdSend } from 'react-icons/md';
import { FaFacebookF, FaInstagram, FaTwitter } from 'react-icons/fa';
import Headers from '../components/Headers';
import Footer from '../components/Footer';

const ContactInfoItem = ({ icon: Icon, title, content, link }) => (
  <div className="flex items-start gap-4 p-6 rounded-2xl bg-white shadow-sm border border-secondary/5 hover:shadow-md transition-all duration-300">
    <div className="size-12 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary flex-shrink-0">
      <Icon size={24} />
    </div>
    <div className="space-y-1">
      <h3 className="font-bold text-primary text-lg">{title}</h3>
      {link ? (
        <a href={link} className="text-textSecondary hover:text-secondary transition-colors text-sm font-medium">
          {content}
        </a>
      ) : (
        <p className="text-textSecondary text-sm font-medium">{content}</p>
      )}
    </div>
  </div>
);

const Contact = () => {
  const formRef = useRef(null);

  const sendEmail = async (e) => {
    e.preventDefault();

    const formData = new FormData(formRef.current);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("https://formsubmit.co/ajax/apnaplaza.store@gmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success('Your message has been sent successfully!');
        formRef.current.reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('There was an error sending your message.');
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Headers />

      <main className="flex-grow pt-28 pb-16">
        <div className="w-[94%] md:w-[95%] mx-auto">
          {/* Header Section */}
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <h1 className="text-5xl md:text-4xl font-extrabold text-primary tracking-tight">
              Get in <span className="text-secondary">Touch</span>
            </h1>
            <div className="h-1.5 w-24 bg-secondary rounded-full mx-auto"></div>
            <p className="text-textSecondary text-lg font-medium opacity-80">
              Have questions or need assistance? We're here to help you connect with the Gul Plaza community.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-10 lg:gap-8">
            {/* Left Column: Contact Info */}
            <div className="col-span-4 lg:col-span-12 space-y-6">
              <div className="bg-card rounded-[32px] p-8 space-y-8 border border-secondary/10">
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-primary">Contact Information</h2>
                  <p className="text-textSecondary text-sm">Fill out the form or reach out via our official channels.</p>
                </div>

                <div className="space-y-4">
                  <ContactInfoItem 
                    icon={MdEmail} 
                    title="Email Us" 
                    content="apnaplaza.store@gmail.com" 
                    link="mailto:apnaplaza.store@gmail.com" 
                  />
                  <ContactInfoItem 
                    icon={MdPhone} 
                    title="WhatsApp" 
                    content="+92 343 2913865" 
                    link="https://wa.me/923432913865" 
                  />
                  <ContactInfoItem 
                    icon={FaInstagram} 
                    title="Instagram" 
                    content="@apnaplazaofficial" 
                    link="https://www.instagram.com/apnaplazaofficial/" 
                  />
                </div>

                {/* Social Links */}
                <div className="pt-6 border-t border-secondary/10">
                  <h3 className="text-sm font-bold text-primary uppercase tracking-wider mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    {[
                      { icon: FaFacebookF, link: "#" },
                      { icon: FaInstagram, link: "https://www.instagram.com/apnaplazaofficial/" },
                      { icon: FaTwitter, link: "#" }
                    ].map((social, i) => (
                      <a 
                        key={i} 
                        href={social.link} 
                        target="_blank"
                        rel="noreferrer"
                        className="size-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-textSecondary hover:bg-secondary hover:text-white transition-all duration-300 border border-secondary/5"
                      >
                        <social.icon size={18} />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="col-span-8 lg:col-span-12">
              <div className="bg-white rounded-[32px] p-10 md:p-6 shadow-sm border border-secondary/5 h-full">
                <form ref={formRef} onSubmit={sendEmail} className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="user_name" className="block text-sm font-bold text-primary ml-1">Full Name</label>
                      <input 
                        type="text" 
                        name="user_name" 
                        id="user_name" 
                        placeholder="John Doe"
                        className="w-full px-5 py-4 rounded-2xl bg-background border border-transparent focus:border-secondary/30 focus:bg-white outline-none transition-all text-textPrimary placeholder:text-textSecondary/40" 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-bold text-primary ml-1">Email Address</label>
                      <input 
                        type="email" 
                        name="email" 
                        id="email" 
                        placeholder="john@example.com"
                        className="w-full px-5 py-4 rounded-2xl bg-background border border-transparent focus:border-secondary/30 focus:bg-white outline-none transition-all text-textPrimary placeholder:text-textSecondary/40" 
                        required 
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="subject" className="block text-sm font-bold text-primary ml-1">Subject</label>
                    <input 
                      type="text" 
                      name="subject" 
                      id="subject" 
                      placeholder="How can we help you?"
                      className="w-full px-5 py-4 rounded-2xl bg-background border border-transparent focus:border-secondary/30 focus:bg-white outline-none transition-all text-textPrimary placeholder:text-textSecondary/40" 
                      required 
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-sm font-bold text-primary ml-1">Your Message</label>
                    <textarea 
                      name="message" 
                      id="message" 
                      rows="6" 
                      placeholder="Write your message here..."
                      className="w-full px-5 py-4 rounded-2xl bg-background border border-transparent focus:border-secondary/30 focus:bg-white outline-none transition-all text-textPrimary placeholder:text-textSecondary/40 resize-none" 
                      required
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="flex items-center justify-center gap-3 w-full sm:w-auto px-10 py-5 bg-secondary text-white font-bold rounded-2xl hover:bg-primary transition-all duration-300 shadow-lg shadow-secondary/20 hover:shadow-primary/20 transform hover:-translate-y-1"
                  >
                    <span>Send Message</span>
                    <MdSend size={20} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
